import { CoreEnv } from '@eventbrite/context-gen';
import { NotificationBar, TYPE_WARNING } from '@eventbrite/eds-notification';
import { useFeatureFlags } from '@eventbrite/feature-flag-utils';
import { GlobalNav } from '@eventbrite/global-nav';
import { gettext } from '@eventbrite/i18n';
import { MlpHeader } from '@eventbrite/mlp-header';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { SiteStructure } from '@eventbrite/site-structure';
import { OrganizerSubNav } from '@eventbrite/wagtail-components';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
    GA_ACTION_PROFILE_DROPDOWN,
    GA_CATEGORY_HEADER,
    GA_LABEL_CREATOR_EVENT,
} from '../constants/analytics';
import './page.scss';
import { getIndependentScrolling } from './utils';

const MLP_HEADER_OVERRIDE = ['/organizer/pricing/'];

type LinksData = {
    pk?: number;
    link?: string;
    title?: string;
};

export interface IMarketingPage extends RouteComponentProps<any, any> {
    env: CoreEnv;
    request: object;
    user: {
        canInvite?: boolean;
        canCreateEvents?: boolean;
        firstName?: string;
        fullName?: string;
        hasEvents?: boolean;
        hasOrganizers?: boolean;
        hasTikibar?: boolean;
        isAttendee?: boolean;
        isAuthenticated?: boolean;
        isStaff?: boolean;
        numOrders?: number;
        numSavedEvents?: number;
        publicId?: string;
        thumbnailUrl?: string;
        numFollowing?: number;
    };
    footerLinks: {
        popularSearches: LinksData[];
        eventsToPlan: LinksData[];
        thirdFooterColumn: LinksData[];
        countries: LinksData[];
        showMinimalLinks: boolean;
        thirdFooterColumnName: string;
    };
    children?: React.ReactChildren;
    featureFlags?: any;
}

const Covid19Notification = () => (
    <NotificationBar
        type={TYPE_WARNING}
        onCallAction={() => null}
        callAction={
            <a href="/l/coronavirus-resources-event-organizers/">
                {gettext('Learn more')}
            </a>
        }
    >
        {gettext(
            'Communities around the world are feeling the effects of the coronavirus (COVID-19) on their live experiences. We’re here to help in any way we can.',
        )}
    </NotificationBar>
);

const MarketingPage = (props: IMarketingPage) => {
    const { env, request, user, footerLinks, children, location } = props;

    const { launchNewPackageAndPricing, enableIaGlobalNav } = useFeatureFlags();
    launchNewPackageAndPricing &&
        MLP_HEADER_OVERRIDE.push('/organizer/compare-packages/');
    const shouldOverrideHeader = MLP_HEADER_OVERRIDE.includes(
        location?.pathname,
    );
    const hasIndependentScrolling = getIndependentScrolling(location);
    const covidNotification = <Covid19Notification />;

    const onTrackingUserDropdown = () => {
        trackAnalyticsEvent({
            action: GA_ACTION_PROFILE_DROPDOWN,
            category: GA_CATEGORY_HEADER,
            label: GA_LABEL_CREATOR_EVENT,
        });
    };

    const getMainHeader = () => {
        if (!user?.isAuthenticated && enableIaGlobalNav) {
            return <GlobalNav user={user} env={env} />;
        }
        if (shouldOverrideHeader) {
            return <MlpHeader user={user} env={env} />;
        }
    };

    const getAdditionalHeader = () => {
        if (!user?.isAuthenticated && enableIaGlobalNav) {
            return <OrganizerSubNav sticky={true} env={env} />;
        }
    };

    return (
        <SiteStructure
            env={env}
            request={request}
            user={user}
            footerLinks={footerLinks}
            showMarketingLinks={true}
            fullScreenMain={true}
            hasIndependentScrolling={hasIndependentScrolling}
            onMouseEnterGlobalHeader={onTrackingUserDropdown}
            globalHeader={getMainHeader()}
            additionalHeaderContent={getAdditionalHeader()}
        >
            {!launchNewPackageAndPricing && covidNotification}
            {children}
        </SiteStructure>
    );
};

export default withRouter(MarketingPage);
